import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../hooks/AppContext';
import { api } from '../utils/api';
import { getCurrentPayMonth, monthToNum, MONTHS } from '../utils/helpers';
import * as XLSX from 'xlsx';

export default function CutoffPage() {
  const { switchMonth } = useApp();
  const [cutoffs, setCutoffs] = useState([]);
  const [editedValues, setEditedValues] = useState({}); // {month_key: datetime}
  const [loading, setLoading] = useState(true);
  const [uploadMsg, setUploadMsg] = useState('');
  const [addMonth, setAddMonth] = useState('Jan');
  const [addYear, setAddYear] = useState('2027');
  const [addDt, setAddDt] = useState('');

  const current = getCurrentPayMonth();

  const loadCutoffs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getCutoffs();
      setCutoffs(data.cutoffs);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { loadCutoffs(); }, [loadCutoffs]);

  const saveCutoff = async (month, datetime) => {
    if (month === current) {
      if (!window.confirm(`Changing cut-off for CURRENT month (${month}). Proceed?`)) return;
    }
    try {
      await api.setCutoff(month, datetime);
      // Sync simulator month
      switchMonth(month === current ? '' : month);
      loadCutoffs();
    } catch (err) { alert(err.message); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const wb = XLSX.read(ev.target.result, { type: 'array' });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
      try {
        const result = await api.uploadCutoffs(rows);
        setUploadMsg(`✅ ${result.count} cut-off date(s) loaded.`);
        setTimeout(() => setUploadMsg(''), 6000);
        loadCutoffs();
      } catch (err) { alert(err.message); }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleExport = () => {
    const data = cutoffs.map(c => ({
      month: c.month_key,
      cutoff_date: new Date(c.cutoff_datetime).toLocaleDateString('en-IN'),
      cutoff_time: new Date(c.cutoff_datetime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cutoffs');
    XLSX.writeFile(wb, 'Current_Cutoffs.xlsx');
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all months to default (25th, 5:00 PM)?')) return;
    try { await api.resetCutoffs(); loadCutoffs(); }
    catch (err) { alert(err.message); }
  };

  const handleAdd = async () => {
    if (!addDt) { alert('Select a date and time.'); return; }
    const key = `${addMonth}-${addYear}`;
    await saveCutoff(key, addDt);
  };

  if (loading) return <div className="wrap"><div className="loading">Loading...</div></div>;

  return (
    <div className="wrap">
      <h2>⏰ Confirmation Cut-off Schedule</h2>
      <p className="sub">Set cut-off dates for each month. Cut-off can be any date — even next month. Unconfirmed godowns after cut-off → EXPIRED.</p>

      <div className="btn-group" style={{marginBottom: 14}}>
        <label className="btn btn-a btn-sm" style={{cursor:'pointer'}}>
          📤 Upload <input type="file" accept=".csv,.xlsx,.xls" onChange={handleUpload} hidden />
        </label>
        <button className="btn btn-o btn-sm" onClick={handleExport}>📥 Export Current</button>
        <button className="btn btn-o btn-sm" onClick={handleReset}>🔄 Reset to Default</button>
      </div>

      {uploadMsg && <div className="success-msg">{uploadMsg}</div>}

      <div className="tbl-wrap">
        <table>
          <thead><tr><th style={{width:150}}>Payment Month</th><th>Cut-off Date & Time</th><th style={{width:100}}>Status</th><th style={{width:80}}>Action</th></tr></thead>
          <tbody>
            {cutoffs.map(c => {
              const isCur = c.month_key === current;
              const d = new Date(c.cutoff_datetime);
              const passed = new Date() > d;
              const localVal = d.toISOString().slice(0, 16);
              return (
                <tr key={c.month_key} style={isCur ? {background: 'var(--amb-bg)', fontWeight: 600} : {}}>
                  <td>
                    <span className="fw-700" style={isCur ? {color: 'var(--acc)'} : {}}>{c.month_key}</span>
                    {isCur && <span className="badge-current">CURRENT</span>}
                  </td>
                  <td>
                    <input type="datetime-local" defaultValue={localVal}
                      className="co-input"
                      onChange={e => setEditedValues(prev => ({...prev, [c.month_key]: e.target.value}))} />
                  </td>
                  <td>
                    <span className={passed ? 'text-red fw-600' : 'text-green fw-600'}>
                      {passed ? 'Passed' : 'Active'}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-p btn-sm" onClick={() => saveCutoff(c.month_key, editedValues[c.month_key] || localVal)}>Save</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add custom month */}
      <div className="card" style={{marginTop: 14, padding: '14px 16px', background: '#fafbfe', border: '1px dashed var(--bdr)'}}>
        <h4 className="text-sm" style={{marginBottom: 4}}>➕ Add Custom Month</h4>
        <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
          <select value={addMonth} onChange={e => setAddMonth(e.target.value)} style={{width:'auto'}}>
            {MONTHS.map(m => <option key={m}>{m}</option>)}
          </select>
          <select value={addYear} onChange={e => setAddYear(e.target.value)} style={{width:'auto'}}>
            {['2024','2025','2026','2027','2028','2029'].map(y => <option key={y}>{y}</option>)}
          </select>
          <input type="datetime-local" value={addDt} onChange={e => setAddDt(e.target.value)} style={{width:'auto'}} />
          <button className="btn btn-p btn-sm" onClick={handleAdd}>Add</button>
        </div>
      </div>
    </div>
  );
}
